"use client";

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { haptics } from "@/lib/utils/haptics";
import type { AiProfile } from "@/types/ai";
import type { MultiChatSession } from "@/lib/db/schema";
import {
  listArchivedSessions,
  deleteSession,
  getKarmaStudyConsent,
  setKarmaStudyConsent,
} from "@/lib/chat/multiChatHistory";
import {
  dispatchKarmaAction,
  KARMA_FUNCTION_ITEMS,
  KARMA_SYSTEM_ITEMS,
  type KarmaDeckItem,
} from "@/lib/karma-ac/actions";

interface KarmaNeuralShelfProps {
  onClose: () => void;
  profiles: AiProfile[];
  onOpenProfile?: (profile: AiProfile) => void;
  onResumeSession?: (session: MultiChatSession) => void;
}

type ShelfTab = "home" | "functions" | "system" | "history";

const TABS: { id: ShelfTab; label: string }[] = [
  { id: "home", label: "Home" },
  { id: "functions", label: "Functions" },
  { id: "history", label: "History" },
  { id: "system", label: "System" },
];

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

// A group of N linked tiles that got closed collapses to one row here — see
// archiveClosedGroup in lib/chat/multiChatHistory.ts for when this fires.
// Expanding shows which ACs were in it; Resume reopens all of them, still
// linked, picking their conversations back up where they left off.
function HistoryBox({ onResumeSession }: { onResumeSession?: (session: MultiChatSession) => void }) {
  const [sessions, setSessions] = useState<MultiChatSession[] | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  const refresh = useCallback(() => {
    listArchivedSessions().then(setSessions).catch(() => setSessions([]));
  }, []);

  useEffect(() => {
    refresh();
    setConsent(getKarmaStudyConsent());
  }, [refresh]);

  const handleDelete = async (id: string) => {
    await deleteSession(id);
    refresh();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3 px-0.5">
        <p className="text-[10px] uppercase tracking-widest opacity-40">Closed multi-chat groups</p>
      </div>

      {sessions === null && <p className="text-[11px] opacity-40 px-0.5">Loading…</p>}
      {sessions?.length === 0 && (
        <p className="text-[11px] opacity-40 px-0.5">
          Nothing here yet — link 2+ tiles, talk to them, then close them all to archive the conversation.
        </p>
      )}

      {sessions?.map((s) => {
        const expanded = expandedId === s.id;
        return (
          <div key={s.id} className="karma-shelf-item karma-glass-subtle mb-2">
            <button
              type="button"
              className="w-full text-left bg-transparent border-none p-0 cursor-pointer"
              onClick={() => setExpandedId(expanded ? null : s.id)}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium line-clamp-1">
                  {s.tileSnapshots.length} tiles · {s.tileSnapshots.map((t) => t.profileName).join(", ")}
                </p>
                <span className="text-[10px] opacity-40 shrink-0">{relativeTime(s.closedAt)}</span>
              </div>
              {s.preview && <p className="text-[11px] opacity-50 line-clamp-1 mt-0.5">{s.preview}</p>}
            </button>

            {expanded && (
              <div className="mt-2 pt-2 border-t border-white/10">
                <ul className="text-[11px] opacity-60 mb-2 pl-3 list-disc">
                  {s.tileSnapshots.map((t) => (
                    <li key={t.tileId}>
                      {t.profileName}
                      {s.primaryTileId === t.tileId ? " — primary" : ""}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="flex-1 text-[11px] font-medium py-1.5 rounded-md"
                    style={{ background: "rgba(193,162,255,0.18)", color: "#C1A2FF" }}
                    onClick={() => {
                      haptics.success();
                      onResumeSession?.(s);
                    }}
                  >
                    Resume
                  </button>
                  <button
                    type="button"
                    className="text-[11px] font-medium py-1.5 px-3 rounded-md text-red-300"
                    style={{ background: "rgba(248,113,113,0.12)" }}
                    onClick={() => handleDelete(s.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="mt-4 pt-3 border-t border-white/10">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => {
              setKarmaStudyConsent(e.target.checked);
              setConsent(e.target.checked);
            }}
            className="mt-0.5"
          />
          <span className="text-[10px] opacity-50 leading-snug">
            Let Karma study conversations I delete from here, instead of permanently erasing them. Off by default —
            deleting a group permanently removes it unless this is on.
          </span>
        </label>
      </div>
    </div>
  );
}

// This is now the one launcher — previously the same handful of
// destinations were also reachable via the Karma Orb's tap/hold-cycling
// decks and the composer bar's Smart Dial. Both are parked; every action
// they offered lives here now, in one place, discoverable without needing
// to learn a gesture.
function DeckGrid({ items, onClose }: { items: KarmaDeckItem[]; onClose: () => void }) {
  return (
    <div className="karma-deck-grid">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className="karma-deck-item"
          title={item.description}
          onClick={() => {
            dispatchKarmaAction(item.id);
            haptics.success();
            onClose();
          }}
        >
          <span className="text-lg leading-none">{item.icon}</span>
          <span className="karma-deck-label text-xs mt-0.5">{item.label}</span>
        </button>
      ))}
    </div>
  );
}

export default function KarmaNeuralShelf({
  onClose,
  profiles,
  onOpenProfile,
  onResumeSession,
}: KarmaNeuralShelfProps) {
  const [tab, setTab] = useState<ShelfTab>("home");

  const dragTile = (e: React.DragEvent, item: {
    id: string;
    name: string;
    icon: string;
    type: "chat" | "project";
    description: string;
    profile?: AiProfile;
  }) => {
    const tile = {
      type: item.type,
      name: item.name,
      icon: item.icon,
      profile: item.profile ?? undefined,
      metadata: { sourceId: item.id, description: item.description },
    };
    e.dataTransfer.setData("application/json", JSON.stringify(tile));
    e.dataTransfer.effectAllowed = "copy";
    haptics.click();
  };

  return (
    <div className="karma-neural-shelf">
      <div className="karma-neural-shelf-header">
        <h3 className="text-sm font-light tracking-[0.15em] uppercase">Neural Shelf</h3>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-lg karma-glass-subtle text-lg leading-none"
        >
          ×
        </button>
      </div>

      <div className="flex gap-1 px-4 pt-2 pb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className="flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium transition-colors"
            style={{
              background: tab === t.id ? "rgba(193,162,255,0.18)" : "transparent",
              color: tab === t.id ? "#C1A2FF" : "rgba(255,255,255,0.5)",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="karma-neural-shelf-content">
        {tab === "home" && (
          <>
            <p className="text-[10px] uppercase tracking-widest opacity-40 mb-3">Your AC instances</p>
            {profiles.map((p) => (
              <motion.div
                key={p.id}
                draggable
                onDragStart={(e) =>
                  dragTile(e as unknown as React.DragEvent, {
                    id: p.id,
                    name: p.name,
                    icon: "💬",
                    type: "chat",
                    description: p.description ?? "Conversation",
                    profile: p,
                  })
                }
                onClick={() => onOpenProfile?.(p)}
                className="karma-shelf-item karma-glass-subtle cursor-pointer"
                whileHover={{ x: 4 }}
                title="Open a new tile for this AC"
              >
                <div className="flex gap-3 items-center">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: p.brand?.primaryColor ?? "#C1A2FF" }}
                  />
                  <div>
                    <p className="text-sm font-medium">{p.name}</p>
                    <p className="text-[11px] opacity-50 line-clamp-1">{p.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}

            <p className="text-[10px] text-center opacity-30 uppercase tracking-widest mt-6">
              Drag to canvas · Mindaloo
            </p>
          </>
        )}

        {tab === "functions" && <DeckGrid items={KARMA_FUNCTION_ITEMS} onClose={onClose} />}
        {tab === "history" && <HistoryBox onResumeSession={onResumeSession} />}
        {tab === "system" && <DeckGrid items={KARMA_SYSTEM_ITEMS} onClose={onClose} />}
      </div>
    </div>
  );
}
